import React from 'react';
import { Pivot, PivotItem, Text, IconButton, Icon, DirectionalHint } from '@fluentui/react';
import ClubImageCircle from '../Common/ClubImageCircle';
import { todosContraTodos, Torneo, Partido, Planilla } from '../../system/SystemClient';
import { genkey } from '../../common/constants';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Callout, Link, mergeStyleSets, FontWeights } from '@fluentui/react';
import { DateTime } from 'luxon';
import { PartidoDesktop } from './PartidoDesktop';
import { PartidoMobile } from './PartidoMobile';

export const HIDDEN_LIMIT_DOWN = 'ms-hiddenLgDown';
export const HIDDEN_LIMIT_UP = 'ms-hiddenXlUp';

export const PartidoFixture = ({
  partido,
  highlightClubId,
}: {
  partido: Partido;
  highlightClubId?: string;
}) => {
  const openCalloutOrOpenVideo = (planillas: Planilla[], openCallout: () => void) => {
    const cantTransmisiones = planillas.filter((planilla) => !!planilla.url_transmision).length;
    if (cantTransmisiones === 1) {
      const unicaTransmision = planillas
        .filter((planilla) => !!planilla.url_transmision)
        .map((planilla) => planilla.url_transmision);
      window.open(unicaTransmision[0], '_blank');
    } else {
      openCallout();
    }
  };

  const openCalloutOrOpenPDF = (planillas: Planilla[], openCallout: () => void) => {
    const cantPDFs = planillas.filter((planilla) => !!planilla.pdf).length;
    if (cantPDFs === 1) {
      const unicoPDF = planillas.filter((planilla) => !!planilla.pdf).map((planilla) => planilla.pdf);
      window.open(unicoPDF[0], '_blank');
    } else {
      openCallout();
    }
  };

  const shouldHighlight = (id: string) => {
    return !!id && !!highlightClubId && +id === +highlightClubId;
  };

  const openMap = (campoJuegoCoords: string) => {
    const coords = campoJuegoCoords.split(';');
    const lat = coords[0];
    const lng = coords[1];
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
  };

  const openMapWithDireccion = (campoJuegoDireccion: string) => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURI(campoJuegoDireccion)}`,
      '_blank'
    );
  };

  const getStylesBy = (id: string): React.CSSProperties | undefined => {
    return {
      fontWeight: shouldHighlight(id) ? 700 : 500,
      color: shouldHighlight(id) ? 'red' : 'black',
    };
  };

  return (
    <div className="ms-Grid-row text-center" style={{ marginTop: 5, marginBottom: 5 }} key={genkey()}>
      {/* =====================================================================
                              VISTA DESKTOP
        =====================================================================*/}
      <PartidoDesktop
        partido={partido}
        openCalloutOrOpenPDF={openCalloutOrOpenPDF}
        openCalloutOrOpenVideo={openCalloutOrOpenVideo}
        openMap={openMap}
        getStylesBy={getStylesBy}
        openMapWithDireccion={openMapWithDireccion}
      />
      {/* =====================================================================
                              VISTA MOBILE
        =====================================================================*/}
      <PartidoMobile
        partido={partido}
        openCalloutOrOpenPDF={openCalloutOrOpenPDF}
        openCalloutOrOpenVideo={openCalloutOrOpenVideo}
        openMap={openMap}
        getStylesBy={getStylesBy}
        openMapWithDireccion={openMapWithDireccion}
      />
    </div>
  );
};

export const PlanillasCallout = ({ partido, labelId }: { partido: Partido; labelId: any }) => {
  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12">
          <Text block variant="mediumPlus" style={{ fontWeight: 600 }} className="text-center" id={labelId}>
            Elegí la planilla que quieras ver
          </Text>
        </div>
        <div className="ms-Grid-col ms-sm12">
          <hr></hr>
        </div>
      </div>
      {partido.planillas.map((planilla) => {
        return (
          <div className="ms-Grid-row mt-1">
            <div className="ms-Grid-col ms-sm1" style={{ paddingRight: 0 }}>
              <ClubImageCircle
                escudo={partido.escudoImagePathLocal}
                width={25}
                height={25}
                right
              ></ClubImageCircle>
            </div>

            <div
              className="ms-Grid-col ms-sm3 text-center"
              style={{
                marginTop: 3,
                fontWeight: 600,
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              {planilla.local.goles} - {planilla.visitante.goles}
            </div>
            <div className="ms-Grid-col ms-sm1" style={{ paddingLeft: 0 }}>
              <ClubImageCircle
                escudo={partido.escudoImagePathVisitante}
                width={25}
                height={25}
              ></ClubImageCircle>
            </div>

            <div className="ms-Grid-col ms-sm7 text-center">
              <PrimaryButton
                disabled={!!!planilla.pdf}
                onClick={() => {
                  window.open(planilla.pdf, '_blank');
                }}
                iconProps={{ iconName: planilla.pdf ? 'AccountActivity' : 'Cancel' }}
                title="Ver planilla"
                styles={{
                  root: {
                    borderRadius: 25,
                    padding: 0,
                    minWidth: 25,
                    height: 27,
                  },
                }}
              ></PrimaryButton>
              <PrimaryButton
                disabled={!!!planilla.url_transmision}
                onClick={() => {
                  window.open(planilla.url_transmision, '_blank');
                }}
                iconProps={{ iconName: planilla.url_transmision ? 'Video' : 'Cancel' }}
                className="ml-2"
                title="Ver transmisión"
                styles={{
                  root: {
                    borderRadius: 25,
                    padding: 0,
                    minWidth: 25,
                    height: 27,
                  },
                }}
              ></PrimaryButton>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const PendienteConfirmacionCallout = () => {
  return (
    <div className="ms-Grid" dir="ltr">
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12">
          <Text block variant="mediumPlus" style={{ fontWeight: 600 }} className="text-center">
            El partido esta pendiente de confirmación
          </Text>
        </div>
      </div>
    </div>
  );
};
